import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain, Share2, Mic, ScanEye, Fingerprint, Rocket, ArrowLeft, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Same hosted mothership photo the actual Landing page renders - this is AVA's real
// interface, not a mockup, so visitors get an honest preview of what's waiting inside.
const MOTHERSHIP_URL = "https://static.prod-images.emergentagent.com/jobs/ad6f2b54-5f29-4014-ae49-25799dca8ff0/images/209bb983de6b6d4c23d21afdb158030f12336fdb2be35f6e0bbae26bb1c89deb.jpeg";

const FEATURES = [
  {
    icon: Brain,
    title: "Brain Console",
    tag: "Claude Sonnet 4.6",
    body: "A living AI entity that holds in-depth, fact-grounded scientific discussion - never fabricates a date, number or name. Switch between Balanced, Analytical, Associative and Research cognitive modes on the fly.",
  },
  {
    icon: Share2,
    title: "Hebbian Knowledge Graph",
    tag: "Hebbian + Bayesian",
    body: "Every conversation strengthens real synaptic connections between concepts - a self-organizing memory that reinforces what matters and lets unused links decay, just like a biological brain.",
  },
  {
    icon: Mic,
    title: "Realtime Voice Orb",
    tag: "Live Camera Vision",
    body: "Speak to AVA voice-to-voice with zero typing. Her orb now sees live through your camera while you talk - ask what she sees and she answers instantly, no hesitation.",
  },
  {
    icon: ScanEye,
    title: "Camera Object Vision",
    tag: "Few-shot CLIP learning",
    body: "Point a camera at anything. AVA checks her trained visual memory first, then reasons about anything new with live web grounding - teach her an object once and she'll know it forever.",
  },
  {
    icon: Fingerprint,
    title: "People Memory",
    tag: "Biometric auto-recognition",
    body: "AVA recognizes faces and voices automatically - no manual tagging. She greets familiar people by name, in the right tone, the moment she sees or hears them.",
  },
  {
    icon: Rocket,
    title: "Docking Bay & Fleet",
    tag: "OCTON Mothership",
    body: "Every OCTON project docks into AVA like a spacecraft returning to its mothership - launch any live project instantly, or ask AVA to open one for you by name.",
  },
];

export default function Preview() {
  const navigate = useNavigate();
  const { setShowLoginModal } = useAuth();

  return (
    <div className="min-h-screen w-full bg-[#030509] grid-texture text-slate-200" data-testid="preview-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button
          data-testid="preview-back-button"
          onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-[11px] font-mono-data uppercase tracking-widest text-slate-500 hover:text-cyan-300 transition-colors mb-10"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Mothership
        </button>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-data uppercase tracking-[0.2em] text-cyan-400/80 border border-cyan-400/25 rounded-full px-3 py-1 mb-5">
            <Sparkles className="w-3 h-3" /> A peek before you dock in
          </span>
          <h1 className="font-alien uppercase tracking-wide text-3xl sm:text-4xl lg:text-5xl text-white mb-4 leading-tight">
            What AVA Can Do
          </h1>
          <p className="text-sm sm:text-base text-slate-400 max-w-2xl leading-relaxed">
            AVA is Doc's autonomous super-brain operating system - a knowledge graph that learns like
            a living mind, a voice that sees, and a mothership that every OCTON project docks into.
            Not ready to sign in? Here's what's waiting inside.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative w-full aspect-[16/7] rounded-2xl overflow-hidden border border-cyan-400/15 mb-16"
          data-testid="preview-mothership-visual"
        >
          <motion.img
            src={MOTHERSHIP_URL}
            alt="AVA's real mothership interface"
            className="w-full h-full object-cover"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          />
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(ellipse 60% 60% at 50% 50%, transparent 50%, #030509 115%)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030509] via-transparent to-[#030509]/40" />
          <span className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 border border-cyan-400/20" data-testid="preview-mothership-live-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-[8px] font-mono-data text-cyan-300 uppercase tracking-widest">Live Interface</span>
          </span>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-16">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`preview-feature-card-${i}`}
                className="glass-panel rounded-2xl p-6 hover:border-cyan-400/25 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center mb-4">
                  <Icon className="w-4.5 h-4.5 text-cyan-300" />
                </div>
                <span className="text-[9px] font-mono-data uppercase tracking-widest text-amber-300/80">{f.tag}</span>
                <h3 className="font-display font-semibold text-white text-base mt-1 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.body}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="glass-panel rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-6"
          data-testid="preview-cta-panel"
        >
          <div>
            <h2 className="font-display font-semibold text-white text-lg mb-1">Ready to dock in?</h2>
            <p className="text-sm text-slate-500">Sign in for full access to the mothership.</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              data-testid="preview-signin-button"
              onClick={() => { navigate("/"); setShowLoginModal(true); }}
              className="px-4 py-2.5 rounded-lg bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-500/25 transition-colors text-xs font-mono-data uppercase tracking-wider"
            >
              Sign In
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
